// BannerForm.jsx
import React, { useEffect, useState } from 'react'
import { Formik, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import { Button, Col, Row } from 'react-bootstrap'
import { useFileUpload } from '../../hooks/useFileUpload'
import FormikControl, { ErrorFeedback } from '../../../components/formikControl/FormikControl'
import FileAllDrop from '../../../components/excelUpload/FileAllDrop'
import UploadFile from '../../../components/excelUpload/UploadFile'
import { handleApiError } from '../../../helpers/errorHandler'
import { createBanner, updateBanner } from './api'
//import { isVideoFile,isVideoPreview } from '../../helpers/fileUtils'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'

const isVideoUrl = (url = '') => {
  return /\.(mp4)$/i.test(url.split('?')[0]) // ignore query params
}

function BannerForm({ initialData = null, initialValues: initialValuesProp = null, onAfterSubmit }) {
  // Support either prop name (ModalWithForm might send initialValues,
  // list code used initialValues={row.original}, while other code used initialData)
  const sourceData = initialValuesProp || initialData || null

  const formType = sourceData ? 'update' : 'add'
  const [localPreview, setLocalPreview] = useState(null)
  const { uploadFile, isUploading, uploadProgress } = useFileUpload()

  // Build initial values from sourceData (or defaults)
  const initialValues = {
    banner_id: sourceData?.banner_id ?? '',
    banner_name: sourceData?.banner_name ?? '',
    banner_status: sourceData?.banner_status ?? '1',
    file: null, // file object for user to upload; keep null for existing media
  }

  useEffect(() => {
    // when editing, set preview to existing image/video URL
    if (sourceData?.banner_image) {
      // assume banner_image is a full URL or path your frontend can request
      setLocalPreview(sourceData.banner_image)
    } else {
      setLocalPreview(null)
    }
  }, [sourceData])

  const validationSchema = Yup.object({
    banner_name: Yup.string().required('Banner name is required'),
    file: Yup.mixed()
      .nullable() // 🟢 NEW allow null explicitly
      .test('file-required', 'File is required', function (value) {
        if (formType === 'add') {
          return !!value // required only in add
        }
        return true // optional in update
      })
      .test('file-type', 'Only images (jpg/png) or mp4 videos allowed', function (file) {
        if (!file) return true
        const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'video/mp4']
        return allowed.includes(file.type)
      }),
  })

  const submitHandler = async (values, { setSubmitting: setLoading }) => {
    setLoading(true)
    const toastId = toast.loading('Submitting')

    try {
      let bannerImage = sourceData?.banner_image || ''
let fileResp = "";
      // If user selected a new file, upload it
      if (values.file) {
        fileResp = await uploadFile(values.file)
        // adjust according to your uploadFile response shape
        bannerImage = fileResp?.data || bannerImage
      }

      const payload = {
        banner_id: values.banner_id || sourceData?.banner_id || '',
        banner_name: values.banner_name,
        banner_status: values.banner_status,
        file: fileResp ? fileResp.data : "",
      }

      const action = formType === 'add' ? createBanner : updateBanner

      await action(payload)
      toast.success('Success', { id: toastId })
      if (typeof onAfterSubmit === 'function') onAfterSubmit()
    } catch (err) {
      handleApiError(err, toastId)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="px-8 py-6">
      <Formik
        initialValues={initialValues}
        enableReinitialize={true} // <- critical for edit form when sourceData loads async
        validationSchema={validationSchema}
        onSubmit={submitHandler}
      >
        {({ setFieldValue, values, isSubmitting, resetForm, setFieldTouched }) => (
          <Form>
            <Row>
              <Col sm="12">
                <FormikControl
                  control="input"
                  name="banner_name"
                  label="Banner Name"
                  placeholder="Enter banner name"
                />
              </Col>

              <Col sm="12" className="mt-3">
                <FormikControl
                  control="check"
                  type="radio"
                  options={[
                    { key: 'Active', value: '1' },
                    { key: 'Inactive', value: '0' },
                  ]}
                  required
                  placeholder="Banner Status"
                  label="Banner Status"
                  name="banner_status"
                />
              </Col>

              <Col sm="12" className="mt-3">
                {values.file ? (
                  <UploadFile
                    file={values.file}
                    removeFile={() => {
                      setFieldValue('file', null)
                      setLocalPreview(sourceData?.banner_image || null)
                    }}
                    isUploading={isUploading}
                    uploadProgress={uploadProgress}
                  />
                ) : (
                  <>
                    <FileAllDrop
                      accept={{ 'image/*': ['.jpeg', '.jpg', '.png'], 'video/*': ['.mp4'] }}
                      onDropRejected={() => toast.error('File not supported. Use .jpg/.png or .mp4')}
                      onDropAccepted={(files) => {
                        const f = files[0]
                        setFieldValue('file', f)
                        setFieldTouched('file', true)

                        // local preview
                        const reader = new FileReader()
                        reader.onload = (e) => setLocalPreview(e.target.result)
                        reader.readAsDataURL(f)
                      }}
                      supportText="Upload image (.jpg/.png) or video (.mp4)."
                    />
                    <ErrorMessage name="file" component={ErrorFeedback} />
                  </>
                )}
              </Col>

              <Col sm="12" className="mt-3">
               {localPreview ? (                 
                  values.file
                    ? values.file.type === 'video/mp4'
                      ? <video src={localPreview} style={{ maxWidth: '300px' }} controls muted loop playsInline />
                      : <img src={localPreview} alt="banner preview" style={{ maxWidth: '300px' }} />
                    : isVideoUrl(localPreview)
                      ? <video src={localPreview} style={{ maxWidth: '300px' }} controls muted loop playsInline />
                      : <img src={localPreview} alt="banner preview" style={{ maxWidth: '300px' }} />
                ) : null}
            </Col>
            </Row>

            <div className="text-center pt-4">
              <FormResetBtn onClick={() => resetForm} disabled={isSubmitting} className="me-4" />              

              <Button variant="primary" type="submit" disabled={isSubmitting || isUploading}>
                {sourceData ? 'Update Banner' : 'Add Banner'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default BannerForm